import { Plus, Trash2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export const PartsEditor = ({ parts, onChange }) => {
  const { t } = useI18n();
  const update = (i, key, value) => onChange(parts.map((p, j) => (j === i ? { ...p, [key]: value } : p)));
  return (
    <div className="space-y-2" data-testid="parts-editor">
      <div className="flex items-center justify-between">
        <Label>{t("parts")}</Label>
        <Button type="button" size="sm" variant="outline" data-testid="add-part-btn" onClick={() => onChange([...parts, { name: "", part_number: "", quantity: 1 }])}>
          <Plus className="w-4 h-4" />{t("addPart")}
        </Button>
      </div>
      {parts.length === 0 && <p className="text-xs text-slate-500">{t("noParts")}</p>}
      {parts.map((p, i) => (
        <div key={p.id || i} className={`flex gap-2 items-center ${p.delivered ? "opacity-60" : ""}`} data-testid={`part-row-${i}`}>
          <Input className="flex-1 min-w-0" placeholder={t("partName")} data-testid={`part-name-${i}`} value={p.name} disabled={p.delivered} onChange={(e) => update(i, "name", e.target.value)} required />
          <Input className="w-28 font-mono" dir="ltr" placeholder={t("partNumber")} data-testid={`part-number-${i}`} value={p.part_number || ""} disabled={p.delivered} onChange={(e) => update(i, "part_number", e.target.value)} />
          <Input className="w-20" type="number" min={1} placeholder={t("quantity")} data-testid={`part-qty-${i}`} value={p.quantity} disabled={p.delivered} onChange={(e) => update(i, "quantity", e.target.value)} required />
          {p.delivered ? (
            <span className="text-xs font-bold text-emerald-700 w-9 text-center" title={p.delivered_voucher}>✓</span>
          ) : (
            <Button type="button" size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-testid={`part-remove-${i}`} onClick={() => onChange(parts.filter((_, j) => j !== i))}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

export const PartsList = ({ parts, testId, showStatus }) => {
  const { t } = useI18n();
  if (!parts?.length) return null;
  return (
    <div data-testid={testId}>
      <div className="text-slate-500 text-xs mb-1">{t("parts")}</div>
      <ul className="text-sm space-y-0.5">
        {parts.map((p, i) => (
          <li key={p.id || i} className="flex justify-between items-center gap-3 border-b border-dashed border-slate-200 py-1" data-testid={`${testId}-item-${i}`}>
            <span className="font-semibold">
              {p.name}
              {p.part_number && <span className="font-mono text-xs text-slate-500 ms-2" dir="ltr">#{p.part_number}</span>}
            </span>
            <span className="flex items-center gap-2">
              {showStatus && (p.returned
                ? <span className="text-[11px] font-bold rounded-full px-2 bg-orange-100 text-orange-800 border border-orange-300">{t("returnedBadge")} {p.delivered_voucher}</span>
                : p.delivered
                ? <span className="text-[11px] font-bold rounded-full px-2 bg-emerald-100 text-emerald-800 border border-emerald-300">{t("delivered")} {p.delivered_voucher}</span>
                : <span className="text-[11px] font-bold rounded-full px-2 bg-rose-100 text-rose-800 border border-rose-300">{t("inShelf")}</span>)}
              <span className="font-mono font-bold">× {p.quantity}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
